import {
  collectExpressionReferences,
  isCalculatedColumn,
  isCalculatedDimension,
  isCalculatedMeasure,
  type SemanticModelIndex,
} from '@aibi/semantic-model';
import { err, ok, type Result } from '@aibi/shared';
import {
  AnalysisPlanError,
  DiagnosticBag,
  hasPlanErrors,
  type AnalysisDiagnostic,
} from './diagnostics.js';

export interface DependencyRef {
  readonly kind: 'measure' | 'dimension' | 'column';
  readonly id: string;
}

/** Cierre transitivo de dependencias de un objeto del modelo. */
export interface DependencyClosure {
  /** Medidas (incluida la propia si es medida), en orden topologico. */
  readonly measureIds: readonly string[];
  /** Columnas calculadas, en orden topologico (dependencias primero). */
  readonly calculatedColumnIds: readonly string[];
  /** Columnas fisicas. */
  readonly columnIds: readonly string[];
  readonly tableIds: readonly string[];
}

export interface ResolvedDependencies extends DependencyClosure {
  readonly closures: readonly {
    readonly ref: DependencyRef;
    readonly closure: DependencyClosure;
  }[];
}

const EMPTY_CLOSURE: DependencyClosure = {
  measureIds: [],
  calculatedColumnIds: [],
  columnIds: [],
  tableIds: [],
};

/** Union ordenada de cierres; conserva el orden topologico de cada uno. */
class ClosureBuilder {
  readonly measureIds = new Set<string>();
  readonly calculatedColumnIds = new Set<string>();
  readonly columnIds = new Set<string>();
  readonly tableIds = new Set<string>();

  merge(closure: DependencyClosure): void {
    for (const id of closure.measureIds) this.measureIds.add(id);
    for (const id of closure.calculatedColumnIds) this.calculatedColumnIds.add(id);
    for (const id of closure.columnIds) this.columnIds.add(id);
    for (const id of closure.tableIds) this.tableIds.add(id);
  }

  build(): DependencyClosure {
    return {
      measureIds: [...this.measureIds],
      calculatedColumnIds: [...this.calculatedColumnIds],
      columnIds: [...this.columnIds],
      tableIds: [...this.tableIds],
    };
  }
}

/**
 * Resuelve dependencias transitivas (medida -> medida -> columna calculada -> columna
 * fisica) con memoria compartida entre referencias y deteccion de ciclos.
 */
export class DependencyResolver {
  readonly #index: SemanticModelIndex;
  readonly #memo = new Map<string, DependencyClosure>();
  readonly #visiting: string[] = [];
  readonly #reportedCycles = new Set<string>();
  readonly #bag = new DiagnosticBag();

  constructor(index: SemanticModelIndex) {
    this.#index = index;
  }

  get diagnostics(): readonly AnalysisDiagnostic[] {
    return this.#bag.items;
  }

  resolve(ref: DependencyRef): DependencyClosure {
    const node = `${ref.kind}:${ref.id}`;
    const memo = this.#memo.get(node);
    if (memo) return memo;
    const at = this.#visiting.indexOf(node);
    if (at >= 0) {
      this.#reportCycle([...this.#visiting.slice(at), node]);
      return EMPTY_CLOSURE;
    }
    this.#visiting.push(node);
    const closure = this.#compute(ref);
    this.#visiting.pop();
    this.#memo.set(node, closure);
    return closure;
  }

  #compute(ref: DependencyRef): DependencyClosure {
    switch (ref.kind) {
      case 'measure':
        return this.#measure(ref.id);
      case 'dimension':
        return this.#dimension(ref.id);
      case 'column':
        return this.#column(ref.id);
    }
  }

  #measure(measureId: string): DependencyClosure {
    const measure = this.#index.getMeasure(measureId);
    if (!measure) return this.#missing('Medida', measureId);
    const builder = new ClosureBuilder();
    if (measure.tableId !== undefined) this.#addTable(builder, measure.tableId);
    if (isCalculatedMeasure(measure)) {
      const refs = collectExpressionReferences(measure.expression);
      for (const id of refs.columnIds) builder.merge(this.resolve({ kind: 'column', id }));
      for (const id of refs.measureIds) builder.merge(this.resolve({ kind: 'measure', id }));
    } else {
      if (measure.columnId !== undefined) {
        builder.merge(this.resolve({ kind: 'column', id: measure.columnId }));
      }
      if (measure.filter) {
        for (const id of collectExpressionReferences(measure.filter).columnIds) {
          builder.merge(this.resolve({ kind: 'column', id }));
        }
      }
    }
    builder.measureIds.add(measureId);
    return builder.build();
  }

  #dimension(dimensionId: string): DependencyClosure {
    const dimension = this.#index.getDimension(dimensionId);
    if (!dimension) return this.#missing('Dimension', dimensionId);
    const builder = new ClosureBuilder();
    if (isCalculatedDimension(dimension)) {
      this.#addTable(builder, dimension.tableId);
      for (const id of collectExpressionReferences(dimension.expression).columnIds) {
        builder.merge(this.resolve({ kind: 'column', id }));
      }
    } else {
      builder.merge(this.resolve({ kind: 'column', id: dimension.columnId }));
    }
    return builder.build();
  }

  #column(columnId: string): DependencyClosure {
    const resolved = this.#index.getColumn(columnId);
    if (!resolved) return this.#missing('Columna', columnId);
    const builder = new ClosureBuilder();
    builder.tableIds.add(resolved.table.id);
    if (isCalculatedColumn(resolved.column)) {
      for (const id of collectExpressionReferences(resolved.column.expression).columnIds) {
        builder.merge(this.resolve({ kind: 'column', id }));
      }
      builder.calculatedColumnIds.add(columnId);
    } else {
      builder.columnIds.add(columnId);
    }
    return builder.build();
  }

  #addTable(builder: ClosureBuilder, tableId: string): void {
    if (this.#index.getTable(tableId)) builder.tableIds.add(tableId);
    else this.#missing('Tabla', tableId);
  }

  #missing(kind: string, id: string): DependencyClosure {
    this.#bag.error('missing-reference', `${kind} inexistente "${id}".`, { objectId: id });
    return EMPTY_CLOSURE;
  }

  #reportCycle(nodes: readonly string[]): void {
    const ids = nodes.map((node) => node.slice(node.indexOf(':') + 1));
    const signature = [...new Set(ids)].sort().join('|');
    if (this.#reportedCycles.has(signature)) return;
    this.#reportedCycles.add(signature);
    this.#bag.error('cyclic-dependency', `Dependencia circular: ${ids.join(' -> ')}.`, {
      objectId: ids[0] ?? '',
    });
  }
}

/**
 * Resuelve el cierre transitivo de dependencias de varias referencias. Falla con
 * `missing-reference` o `cyclic-dependency` antes de cualquier planificacion.
 *
 * @example
 * const deps = resolveDependencies([{ kind: 'measure', id: 'avg_ticket' }], index);
 * if (deps.ok) deps.value.columnIds; // ['orders.amount']
 */
export function resolveDependencies(
  refs: readonly DependencyRef[],
  index: SemanticModelIndex,
): Result<ResolvedDependencies, AnalysisPlanError> {
  const resolver = new DependencyResolver(index);
  const union = new ClosureBuilder();
  const closures = refs.map((ref) => {
    const closure = resolver.resolve(ref);
    union.merge(closure);
    return { ref, closure };
  });
  const { diagnostics } = resolver;
  if (hasPlanErrors(diagnostics)) {
    return err(new AnalysisPlanError('No se pudieron resolver las dependencias.', diagnostics));
  }
  return ok({ ...union.build(), closures });
}
